import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";

const NAV_LINKS = [
  { label: "About", href: "/#about" },
  { label: "Journals", href: "/#journals" },
  { label: "Submit", href: "/#submit" },
  { label: "Editorial Board", href: "/editorial-board/ijmrcr" },
  { label: "Standards", href: "/#standards" },
  { label: "Contact", href: "/#contact" },
];

function dispatchCmdK() {
  const evt = new KeyboardEvent("keydown", {
    key: "k",
    ctrlKey: true,
    metaKey: true,
    bubbles: true,
  });
  window.dispatchEvent(evt);
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().includes("MAC");

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[var(--sci-border)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <div className="w-9 h-9 border border-[var(--sci-navy)] flex items-center justify-center">
              <span className="font-serif-display text-[var(--sci-navy)] text-xl leading-none">
                S
              </span>
            </div>
            <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-[var(--sci-teal)]" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-[var(--sci-navy)] text-base tracking-tight">
              Sci Ink Press
            </span>
            <span className="overline text-[var(--sci-text-muted)] text-[10px]">
              International · Est. 2018
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => {
            const isRoute = l.href.startsWith("/") && !l.href.startsWith("/#");
            const Comp = isRoute ? Link : "a";
            const props = isRoute ? { to: l.href } : { href: l.href };
            return (
              <Comp
                key={l.label}
                {...props}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-[var(--sci-text)]/80 hover:text-[var(--sci-teal)] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-px after:bg-[var(--sci-teal)] hover:after:w-full after:transition-all"
              >
                {l.label}
              </Comp>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={dispatchCmdK}
            data-testid="navbar-search"
            aria-label="Open command palette"
            className="hidden md:inline-flex items-center gap-2 border border-[var(--sci-border)] hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] px-3 py-2 text-xs text-[var(--sci-text-muted)] transition-colors bg-white/60"
          >
            <Search size={13} />
            <span className="font-mono-sci tracking-wider">Search</span>
            <kbd className="font-mono-sci text-[10px] tracking-wider border border-[var(--sci-border)] px-1.5 py-0.5">
              {isMac ? "⌘K" : "Ctrl K"}
            </kbd>
          </button>

          <a
            href="/#submit"
            data-testid="nav-cta-submit"
            className="hidden md:inline-flex btn-primary px-5 py-2.5 text-sm tracking-wide"
          >
            Submit
          </a>
          <button
            data-testid="navbar-mobile-toggle"
            className="lg:hidden p-2 text-[var(--sci-navy)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[var(--sci-border)]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => {
              const isRoute = l.href.startsWith("/") && !l.href.startsWith("/#");
              const Comp = isRoute ? Link : "a";
              const props = isRoute ? { to: l.href } : { href: l.href };
              return (
                <Comp
                  key={l.label}
                  {...props}
                  data-testid={`nav-link-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="text-base text-[var(--sci-text)] hover:text-[var(--sci-teal)]"
                >
                  {l.label}
                </Comp>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                dispatchCmdK();
              }}
              data-testid="navbar-search-mobile"
              className="inline-flex items-center gap-2 border border-[var(--sci-border)] px-4 py-2.5 text-sm text-[var(--sci-text-muted)] mt-1 self-start"
            >
              <Search size={13} />
              Search the site
            </button>
            <a
              href="/#submit"
              onClick={() => setOpen(false)}
              data-testid="nav-cta-submit-mobile"
              className="btn-primary px-5 py-3 text-sm w-full text-center mt-2"
            >
              Submit Manuscript
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
